"use client";

import mapValues from "lodash/mapValues";
import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";

import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import { submitFunction, useFormValidation } from "@/lib/formValidationHelpers";
import { getZoneName } from "@/lib/getZoneName";
import { useAuth } from "@/lib/useAuth";
import { useData, useDataList } from "@/lib/useData";
import { useValidateAddress } from "@/lib/useValidateAddress";
import PartAddressSuggestions from "@/PartAddressSuggestions";
import PartFormDatePicker from "@/PartFormDatePicker";
import PartFormError from "@/PartFormError";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";
import { Address, Zone } from "@/types/api-types";
import { addressEditorSchemaJoi } from "@/validations";

declare global {
  interface Window {
    google: typeof google;
  }
}

type IFormInput = Address;

interface IFormInputRef {
  reset: () => void;
}

export const useModalAddressImportEditor = factoryUseModalX({
  queryName: "modalAddressImportEditor",
});

export const AddressImportEditor = React.forwardRef<
  IFormInputRef,
  { setConfirmClose?: (arg0: boolean) => void }
>(({ setConfirmClose }, ref) => {
  const { isEditing, editingId, finishInteraction } = useModalAddressImportEditor();

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
  });

  const {
    register,
    formState: { errors, isDirty },
    reset,
    watch,
    control,
    trigger,
    setError,
    clearErrors,
  } = formMethods;

  React.useImperativeHandle(ref, () => ({
    reset: () => reset(),
  }));

  const { handleSubmit, isSubmitting, submitRunner } = useFormValidation<IFormInput>({
    control,
    joiSchema: addressEditorSchemaJoi,
    context: { isEditing, editingId },
    setError,
    clearErrors,
  });

  const [existingItem, existingItemLoading] = useData<{ address: Address }>(
    (() => {
      if (isEditing)
        return process.env.NEXT_PUBLIC_API_URL + `/api/v1/import-rows/${editingId}`;
      return "";
    })(),
  );

  const [programsItems, programsItemsLoading] = useDataList<{ id: number; name: string }>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/program",
  );

  const isZonesLoading = !existingItem?.address?.zone;
  const zones = React.useMemo(
    () => [existingItem?.address?.zone].filter(Boolean) as Zone[],
    [existingItem],
  );

  React.useEffect(() => {
    if (isZonesLoading) return;

    if (isEditing && existingItem) {
      const existingItem1 = existingItem?.address;

      const existingItem2 = {
        ...existingItem1,
        zone_id: (() => {
          if (existingItem1?.zone_id) return existingItem1?.zone_id;
          if (existingItem1?.zone) return existingItem1.zone.id;
        })(),
      };
      reset(existingItem2);
    }
  }, [isEditing, editingId, existingItem, zones, isZonesLoading]);

  const { idToken } = useAuth();

  const { startValidate, isValidateRunning } = useValidateAddress({
    submitRunner,
    isEditing,
    editingId,
  });

  const onSubmit: submitFunction<IFormInput> = async (data, handleApiResult) => {
    let url = process.env.NEXT_PUBLIC_API_URL as string;
    if (isEditing) url += "/api/v1/import-rows/" + editingId;

    const result = await fetchSingleton(url, {
      method: (isEditing && "PUT") || "GET",
      body: JSON.stringify({
        ...data,
        is_direct: false,
        ...(isEditing ? { is_approved: true } : {}),
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });

    handleApiResult(result);

    const msg = (() => {
      if (isEditing) return "Address updated successfully";
    })();

    toast.success(msg, { autoClose: 5000 });

    finishInteraction();
  };

  React.useEffect(() => {
    if (setConfirmClose) setConfirmClose(isDirty);
  }, [isDirty]);

  return (
    <FormProvider {...formMethods}>
      <PartLoader
        block
        active={existingItemLoading || programsItemsLoading || isZonesLoading}
      >
        <form
          className={formStyles.form}
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            finishInteraction();
          }}
          autoComplete="off"
        >
          <label className={formStyles.formLabel} htmlFor="address_suggestions">
            Address Autocomplete
          </label>
          <PartAddressSuggestions
            id="address_suggestions"
            onChange={addressSuggestion => {
              mapValues(addressSuggestion, (v, k) => {
                // @ts-expect-error no definition
                setValue(k, v);
                // @ts-expect-error no definition
                trigger(k).then();
              });
            }}
          />
          <hr className={formStyles.formHLine} />
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="unit">
                Unit
              </label>
              <input
                id="unit"
                className={formStyles.formInput}
                {...register("unit")}
                aria-invalid={errors.unit ? "true" : "false"}
              />
              <PartFormError name="unit" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="street_number">
                Street Number <sup>*</sup>
              </label>
              <input
                id="street_number"
                className={formStyles.formInput}
                {...register("street_number")}
                aria-invalid={errors.street_number ? "true" : "false"}
              />
              <PartFormError name="street_number" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="street_full_name">
                Street Name <sup>*</sup>
              </label>
              <input
                id="street_full_name"
                className={formStyles.formInput}
                {...register("street_full_name")}
                aria-invalid={errors.street_full_name ? "true" : "false"}
              />
              <PartFormError name="street_full_name" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="city">
                City <sup>*</sup>
              </label>
              <input
                id="city"
                className={formStyles.formInput}
                {...register("city")}
                aria-invalid={errors.city ? "true" : "false"}
              />
              <PartFormError name="city" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="program_id">
                Province <sup>*</sup>
              </label>
              <div className={formStyles.formSelect}>
                <select
                  id="program_id"
                  data-placeholder={!watch("program_id") ? "" : undefined}
                  {...register("program_id")}
                  aria-invalid={errors.program_id ? "true" : "false"}
                >
                  <option hidden value="">
                    Please Choose...
                  </option>
                  {programsItems?.map(program => (
                    <option value={String(program?.id)} key={program?.id}>
                      {program?.name}
                    </option>
                  ))}
                </select>
              </div>
              <PartFormError name="program_id" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="postal_code">
                Postal Code <sup>*</sup>
              </label>
              <input
                id="postal_code"
                className={formStyles.formInput}
                {...register("postal_code")}
                aria-invalid={errors.postal_code ? "true" : "false"}
              />
              <PartFormError name="postal_code" />
            </div>
          </div>
          <hr className={formStyles.formHLine} />
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="curbside_households">
                Curbside Households <sup>*</sup>
              </label>
              <input
                id="curbside_households"
                className={formStyles.formInput}
                {...register("curbside_households")}
                aria-invalid={errors.curbside_households ? "true" : "false"}
              />
              <PartFormError name="curbside_households" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="collection_route">
                Collection Route
              </label>
              <input
                id="collection_route"
                className={formStyles.formInput}
                {...register("collection_route")}
                aria-invalid={errors.collection_route ? "true" : "false"}
              />
              <PartFormError name="collection_route" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label
                className={formStyles.formLabel}
                htmlFor="collection_household_start_date"
              >
                Collection Household Start Date
              </label>
              <PartFormDatePicker
                id="collection_household_start_date"
                control={control}
                name={"collection_household_start_date"}
                placeholder="Select Date"
                // Dec 2, 2024 (Tuesday)
                dateFormat="MMM d, yyyy (EEE)"
              />
              <PartFormError name="collection_household_start_date" />
            </div>
          </div>
          <hr className={formStyles.formHLine} />
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="lat">
                Latitude {/* <sup>*</sup> */}
              </label>
              <input
                id="lat"
                className={formStyles.formInput}
                {...register("lat")}
                aria-invalid={errors.lat ? "true" : "false"}
              />
              <PartFormError name="lat" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="lon">
                Longitude {/* <sup>*</sup> */}
              </label>
              <input
                id="lon"
                className={formStyles.formInput}
                {...register("lon")}
                aria-invalid={errors.lon ? "true" : "false"}
              />
              <PartFormError name="lon" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="zone_id">
                Zone <sup>*</sup>
              </label>
              <div className={formStyles.formSelect}>
                <select
                  id="zone_id"
                  data-placeholder={!watch("zone_id") ? "" : undefined}
                  {...register("zone_id")}
                  aria-invalid={errors.zone_id ? "true" : "false"}
                >
                  <option hidden value="">
                    Please Choose...
                  </option>
                  {zones?.map((zone: Zone) => (
                    <option value={zone?.id} key={zone?.id}>
                      {(() => {
                        let text = getZoneName(zone);
                        if (text.length >= 65) text = text.slice(0, 65) + "...";
                        return text;
                      })()}
                    </option>
                  ))}
                </select>
              </div>
              <PartFormError name="zone_id" />
            </div>
          </div>
          <label className={formStyles.formLabel} htmlFor="notes">
            Notes
          </label>
          <textarea
            id="notes"
            className={formStyles.formTextarea}
            placeholder="e.g. Address may need to be validated manually."
            {...register("notes")}
            aria-invalid={errors.notes ? "true" : "false"}
          />
          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="reset"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
            >
              Discard
            </button>
            <button
              type="button"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="validate"
              onClick={startValidate}
            >
              <PartLoader active={isValidateRunning}>Check</PartLoader>
            </button>
            <PartFormSaveButton isSubmitting={isSubmitting}>
              Save as Verified
            </PartFormSaveButton>
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
});

AddressImportEditor.displayName = "AddressImportEditor";

const ModalAddressImportEditor = React.forwardRef<PartModalRef>((props, ref) => {
  const { isEditing, finishInteraction } = useModalAddressImportEditor();

  const title = (isEditing && "Edit Imported Address") || "Unknown action";

  const [shouldConfirmClose, setShouldConfirmClose] = React.useState<boolean>(false);

  const formRef = React.useRef<IFormInputRef>(null);

  return (
    <PartModal
      ref={ref}
      title={title}
      showed={isEditing}
      onClose={() => finishInteraction()}
      afterClose={() => formRef.current?.reset()}
      confirmCloseQuestion={shouldConfirmClose}
    >
      <AddressImportEditor setConfirmClose={setShouldConfirmClose} ref={formRef} />
    </PartModal>
  );
});

ModalAddressImportEditor.displayName = "ModalAddressImportEditor";

export default ModalAddressImportEditor;
